import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';

import { openConfirmationDialogAction } from 'store/global/actions';
import {
  deleteServicingPaymentAction,
  getServicingPaymentScheduleAction,
  getServicingSummaryAction,
  reverseServicingPaymentAction,
} from 'store/loanApplications/actions';
import {
  selectServicingPayments,
  selectServicingPaymentSchedule,
} from 'store/loanApplications/selectors';

import { getPaymentHistoryTableConfig } from '../Servicing.config';

import PaymentDrawer from './PaymentDrawer';
import { headerStyles } from './PaymentHistory.styles';
import { PaymentHistoryTestIds } from './PaymentHistory.testIds';

const PaymentHistory = ({ isLoading, hasEscrow = false }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const payments = useSelector(selectServicingPayments);
  const paymentSchedule = useSelector(selectServicingPaymentSchedule);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  const onEdit = useCallback(row => {
    setEditingPayment(row);
    setIsDrawerOpen(true);
  }, []);

  const onDelete = useCallback(
    row => {
      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: 'Are you sure you want to delete this payment?',
          onAcceptButtonName: 'Delete',
          onAccept: () => {
            dispatch(
              deleteServicingPaymentAction({
                applicationId,
                paymentId: row.id,
              }),
            ).then(() => {
              dispatch(getServicingSummaryAction({ applicationId }));
              dispatch(getServicingPaymentScheduleAction({ applicationId }));
            });
          },
        }),
      );
    },
    [dispatch, applicationId],
  );

  const onReverse = useCallback(
    row => {
      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: 'Are you sure you want to reverse this payment?',
          onAcceptButtonName: 'Reverse',
          onAccept: () => {
            dispatch(
              reverseServicingPaymentAction({
                applicationId,
                paymentId: row.id,
                reversalReason: 'Manual reversal',
              }),
            ).then(() => {
              dispatch(getServicingSummaryAction({ applicationId }));
              dispatch(getServicingPaymentScheduleAction({ applicationId }));
            });
          },
        }),
      );
    },
    [dispatch, applicationId],
  );

  const tableConfig = useMemo(
    () => getPaymentHistoryTableConfig({ onEdit, onDelete, onReverse, isLoading, hasEscrow }),
    [onEdit, onDelete, onReverse, isLoading, hasEscrow],
  );

  const onRecordPayment = useCallback(() => {
    setEditingPayment(null);
    setIsDrawerOpen(true);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingPayment(null);
  }, []);

  const onSuccess = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingPayment(null);
    dispatch(getServicingSummaryAction({ applicationId }));
    dispatch(getServicingPaymentScheduleAction({ applicationId }));
  }, [dispatch, applicationId]);

  return (
    <>
      <Box sx={headerStyles}>
        <Button
          color="primary"
          data-testid={PaymentHistoryTestIds.RECORD_BUTTON}
          disabled={isLoading}
          variant="contained"
          onClick={onRecordPayment}
        >
          Record Payment
        </Button>
      </Box>

      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        data={payments}
        isLoading={isLoading}
        skeletonRows={5}
      />

      <PaymentDrawer
        applicationId={applicationId}
        isOpen={isDrawerOpen}
        payment={editingPayment}
        paymentSchedule={paymentSchedule}
        onClose={onCloseDrawer}
        onSuccess={onSuccess}
      />
    </>
  );
};

PaymentHistory.propTypes = {
  hasEscrow: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default memo(PaymentHistory);
